import { z } from "zod";

import { INVITATION_INBOX_KINDS } from "./invitationInbox";

/**
 * Yol (path) parametresi UUID şemaları (güvenlik/dayanıklılık denetimi bulgusu).
 *
 * ### Neden var
 *
 * `requireTenant` `x-tenant-id`'yi DB'ye vermeden önce UUID biçimini doğruluyor ve
 * gerekçesi açık: bozuk bir değer `uuid` kolonuna gidince Postgres `22P02` ile patlar —
 * kullanıcı girdisiyle tetiklenen bir **500** doğar (yanlış alarm) ve saldırgan 500/404
 * farkından geçerli kimlik biçimini öğrenir. Aynı koruma İKİNCİL path parametrelerinde
 * (membershipId, inviteId, roles/files/device `:id`) yoktu; bu şemalar onu `validate()`
 * ile her uca taşıyor. Bozuk biçim artık DB'ye hiç gitmeden 422 döner.
 *
 * ⚠ `validate()` params şemasını `.strict()`'liyor: şema o rotanın TÜM path
 * parametrelerini içermeli (fazlası reddedilir, eksiği "zorunlu" hatası verir). Bu yüzden
 * tenant alt-kaynak rotalarında `id` (tenant) DE tanımlı — `requireTenant` onu ayrıca
 * doğrulasa bile `c.req.param()` onu da döndürüyor.
 */
const uuid = z.uuid("Geçersiz kimlik biçimi");

/** Tek `:id` parametresi (files, roles, session/devices). */
export const idParamSchema = z.object({ id: uuid });

/** Tenant alt-kaynağı: `:id` (tenant) + `:membershipId`. */
export const membershipParamsSchema = z.object({ id: uuid, membershipId: uuid });

/** Tenant alt-kaynağı: `:id` (tenant) + `:inviteId`. */
export const inviteParamsSchema = z.object({ id: uuid, inviteId: uuid });

/** Sözleşme alt-kaynağı: `:id` (sözleşme) + `:versionId` (CLOS-135 `[090]`). */
export const contractVersionParamsSchema = z.object({ id: uuid, versionId: uuid });

/**
 * Versiyon artifact'ı: `:id` (sözleşme) + `:versionId` + `:artifactType` (CLOS-1236).
 *
 * ⚠ `contractVersionParamsSchema` BURADA KULLANILAMAZ: `validate()` şemayı `.strict()`
 * yapıyor ve üçüncü parametre "tanınmayan alan" diye 422'ye düşerdi. Aynı tuzağa bu depoda
 * üç kez düşüldü; her çok parametreli rota kendi şemasını ister.
 *
 * ⚠ Tür SERBEST METİN DEĞİL: yolun kendisi bir enum. Serbest bıraksaydık, ileride
 * eklenecek bir artifact türü hiçbir kapı değiştirmeden indirilebilir hâle gelirdi.
 */
export const contractVersionArtifactParamsSchema = z.object({
  id: uuid,
  versionId: uuid,
  artifactType: z.enum(
    ["canonical", "redline", "materialized"],
    "Geçerli bir belge türü gereklidir",
  ),
});

/** Müvekkil portföyü: `:id` (müvekkil) + `:contractId` (CLOS-173 `[120]`). */
export const clientContractParamsSchema = z.object({ id: uuid, contractId: uuid });

/** Müvekkil iletişim kişisi: `:id` (müvekkil) + `:contactId` (CLOS-174 `[121]`). */
export const clientContactParamsSchema = z.object({ id: uuid, contactId: uuid });

/** Temsilci daveti: `:id` (müvekkil) + `:inviteId` (CLOS-177 `[124]`). */
export const representativeInvitationParamsSchema = z.object({ id: uuid, inviteId: uuid });

/** Müvekkil temsil ilişkisi: `:id` (müvekkil) + `:repId` (CLOS-178 `[132]`). */
export const clientRepresentationParamsSchema = z.object({ id: uuid, repId: uuid });

/** Haricî katılımcı daveti: `:id` (sözleşme) + `:inviteId` (CLOS-148 `[129]`). */
export const participantInvitationParamsSchema = z.object({ id: uuid, inviteId: uuid });

/** Hizmet ilişkisi: `:id` (müvekkil) + `:relId` (CLOS-246 `[133]`). */
export const clientServiceRelationshipParamsSchema = z.object({ id: uuid, relId: uuid });

/** Portföy sorumluluğu: `:id` (müvekkil) + `:paId` (CLOS-247 `[135]`). */
export const clientPortfolioParamsSchema = z.object({ id: uuid, paId: uuid });

/** Sözleşme yorumu: `:id` (sözleşme) + `:commentId` (CLOS-150 `[137]`). */
export const contractCommentParamsSchema = z.object({ id: uuid, commentId: uuid });

/** Çalışma nesnesi: `:id` (sözleşme) + `:itemId` (CLOS-154 `[140]`). */
export const contractWorkItemParamsSchema = z.object({ id: uuid, itemId: uuid });

/** Nesne paylaşımı: `:id` (sözleşme) + `:shareId` (CLOS-251 `[144]`). */
export const contractObjectShareParamsSchema = z.object({ id: uuid, shareId: uuid });

/**
 * Sözleşme tarafı: `:id` (sözleşme) + `:partyId` (CLOS-1117 · yetim uç H).
 *
 * ⚠ `PUT/DELETE …/parties/:partyId` bugüne kadar params doğrulamadan geçiyordu: bozuk bir
 * `partyId` DB'de `22P02` ile 500 üretiyordu. Şema aynı boşluğu diğer alt-kaynaklarla aynı
 * biçimde kapatıyor; bozuk biçim artık DB'ye gitmeden 422 döner.
 */
export const contractPartyParamsSchema = z.object({ id: uuid, partyId: uuid });

/** Müzakere: `:id` (sözleşme) + `:negId` (CLOS-252 `[145]`). */
export const contractNegotiationParamsSchema = z.object({ id: uuid, negId: uuid });

/** Müzakere öğesi: `:id` + `:negId` + `:itemId` (CLOS-252 `[145]`). */
export const contractNegotiationItemParamsSchema = z.object({
  id: uuid,
  negId: uuid,
  itemId: uuid,
});

/**
 * Davetlerim merkezi: `:kind` (davet ailesi) + `:id` (CLOS-227 `[146]`).
 *
 * `kind` kapalı bir enum — bilinmeyen aile 422'ye düşer, sessizce "bulunamadı"ya değil:
 * merkez üç kanonik akışın yönlendiricisidir, dördüncü bir aile ancak kendi kartıyla
 * ve bu satıra dokunarak eklenebilir.
 */
export const invitationInboxParamsSchema = z.object({
  /**
   * ⚠ Beş anahtar TEK sözlükten (`INVITATION_INBOX_KINDS`, CLOS-1120): dispatcher, Davetlerim
   * projection'ı ve bu şema aynı listeyi okur — bir aile eklenince üçü birden görür.
   */
  kind: z.enum(INVITATION_INBOX_KINDS, { message: "Geçersiz davet türü" }),
  id: uuid,
});

/** AI analizi: `:id` (sözleşme) + `:analysisId` (KARAR 07). */
export const contractAiAnalysisParamsSchema = z.object({ id: uuid, analysisId: uuid });

/** AI risk taraması: `:id` (sözleşme) + `:runId` (çalışma) — CLOS-206. */
export const contractAiRiskRunParamsSchema = z.object({ id: uuid, runId: uuid });
/** AI revizyon çalışması: `:id` (sözleşme) + `:runId` — CLOS-207. */
export const contractAiRevisionRunParamsSchema = z.object({ id: uuid, runId: uuid });
/** AI risk bulgusu: `:id` (sözleşme) + `:findingId` (bulgu) — CLOS-206/1002. */
export const contractAiRiskFindingParamsSchema = z.object({ id: uuid, findingId: uuid });

/** İnceleme akışı: `:id` (sözleşme) + `:reviewId` (CLOS-155 `[148]`). */
export const contractReviewParamsSchema = z.object({ id: uuid, reviewId: uuid });

/** İnceleme görevi: `:id` (sözleşme) + `:reviewId` + `:taskId` (CLOS-157 `[150]`). */
export const contractReviewTaskParamsSchema = z.object({
  id: uuid,
  reviewId: uuid,
  taskId: uuid,
});

/** Onay akışı: `:id` (sözleşme) + `:approvalId` (CLOS-158 `[151]`). */
export const contractApprovalParamsSchema = z.object({ id: uuid, approvalId: uuid });

/** Onay görevi: `:id` + `:approvalId` + `:taskId` (CLOS-159 `[152]`). */
export const contractApprovalTaskParamsSchema = z.object({
  id: uuid,
  approvalId: uuid,
  taskId: uuid,
});

/** Mutabakat akışı: `:id` (sözleşme) + `:reconciliationId` (CLOS-161 `[154]`). */
export const contractReconciliationParamsSchema = z.object({
  id: uuid,
  reconciliationId: uuid,
});

/** Mutabakat görevi: `:id` + `:reconciliationId` + `:taskId` (CLOS-162 `[155]`). */
export const contractReconciliationTaskParamsSchema = z.object({
  id: uuid,
  reconciliationId: uuid,
  taskId: uuid,
});

/** İmza talebi: `:id` (sözleşme) + `:requestId` (CLOS-164 `[157]`). */
export const contractSignatureRequestParamsSchema = z.object({ id: uuid, requestId: uuid });
export const contractSignerParamsSchema = z.object({ id: uuid, requestId: uuid, signerId: uuid });
